/**
 * Access control — the vocabulary.
 *
 * ONE question is asked everywhere in the product: `can(capability)`. This file
 * defines what may be asked (`Capability`) and what the answer is computed from
 * (`AccessSubject`). The answers themselves live in `access.policy.ts`.
 *
 * Before this module, "what can this user see?" was answered five different
 * ways, none of which knew about the others:
 *
 *   1. Org role      — `useUserRole()`, plus ad-hoc `['front-of-house', …]`
 *                      arrays inlined in the sidebar, profile nav, venue
 *                      overview and bookings landing.
 *   2. Internal staff — 42 hand-rolled `email.endsWith('@stampede.ai')` checks,
 *                      sometimes paired with `user.role === 0`, once with 2FA.
 *   3. Feature flags  — `useFeatureFlags()`, 13 flags, 12 of them the identical
 *                      staff-email check copy-pasted.
 *   4. Plan           — `subscription.features` via `useNeedsSubscription`,
 *                      surfaced only as a page-level `RequiresUpgradeNotice`,
 *                      invisible to the nav.
 *   5. Account shape  — `NavContext.isVisible` predicates (Square, data
 *                      providers, hardware tier, paid venue).
 *
 * The consequence of five mechanisms was drift. `NavItem.deniedRoles` was
 * declared but never set and never read, so the v4 sidebar performed NO role
 * gating at all while the old sidebar still did (via `useMenu`'s `denyByKey`) —
 * meaning role-based hiding silently vanished as `newUi` rolled out. The two
 * `NavContext` builders had already been bitten by the same class of bug.
 *
 * So: one vocabulary, one subject, one policy table. Nav items, route guards
 * and in-page conditionals all resolve through it, and none of them can drift
 * from the others.
 */
import type { UserRoleType } from '@/state/auth/auth.types'
import type { HardwareTier } from '@/hooks/use-hardware-tier'
import type { VendorType } from '@/state/wifi/types'

/** Stable org-role identifier. Prefer this over `name` / `legacy_id`. */
export type RoleKey = UserRoleType['key']

/**
 * Top-level product sections. These are the keys `useMenu`'s `denyByKey` map
 * has always been written in terms of, kept identical so the ported role gating
 * is provably behaviour-preserving.
 */
export type SectionKey =
  | 'admin'
  | 'home'
  | 'venues'
  | 'wi-fi'
  | 'bookings'
  | 'reviews'
  | 'marketing'
  | 'inbox'
  | 'loyalty'
  | 'gift-cards'
  | 'reports'
  | 'brand'
  | 'scanner'
  | 'chat'

/**
 * Everything that can be gated, as one flat vocabulary.
 *
 * Naming is `domain.thing` or `domain.thing.action`. Four families:
 *
 *   `nav.*`      — a top-level section is reachable at all (role-driven).
 *   `internal.*` — Stampede staff surfaces. Never visible to customers.
 *   `plan.*`     — the org's subscription includes a paid feature.
 *   everything else — a specific feature or action within a section.
 *
 * Add a capability here, give it a rule in `POLICY`, and it is immediately
 * askable from a nav item, a route and a component with no further wiring.
 */
export type Capability =
  // ── Sections (role-gated; ported verbatim from `useMenu`'s denyByKey) ──
  | 'nav.admin'
  | 'nav.home'
  | 'nav.venues'
  | 'nav.wi-fi'
  | 'nav.bookings'
  | 'nav.reviews'
  | 'nav.marketing'
  | 'nav.inbox'
  | 'nav.loyalty'
  | 'nav.gift-cards'
  | 'nav.reports'
  | 'nav.brand'
  | 'nav.scanner'
  | 'nav.chat'
  /**
   * Org-level tooling with no `denyByKey` precedent: Square POS, Email Audit,
   * Marketplace. Gated only to keep them away from the floor-staff roles, who
   * are meant to see their operational surfaces and nothing else. Deliberately
   * NOT narrowed further, so no role that sees them today loses them.
   */
  | 'nav.org-tools'
  /**
   * Hardware. Separate from `nav.org-tools` precisely because IT Support keeps
   * it — diagnosing kit is their job.
   */
  | 'nav.hardware'
  /**
   * Ordering: the Mouse-backed section (orders, menus, products, modifiers,
   * tables and QR). Whether the ORG has ordering at all is answered by Mouse
   * (`useOrderingCapabilities`), not here; this is the role half of the gate.
   */
  | 'nav.ordering'
  /**
   * What's new: the product changelog. Every org role sees it, so this is not
   * a restriction but the gate the nav invariant requires — an ungated section
   * renders for a subject we know nothing about yet.
   */
  | 'nav.whats-new'
  /**
   * Referrals: the customer referral programme page. Every org role sees it,
   * same reasoning as `nav.whats-new` — the gate exists only for the nav
   * invariant, not to restrict who sees it.
   */
  | 'nav.referrals'
  /**
   * Customers: the top-level group holding customer Search (every role) and
   * Know your customer (which carries its own `kyc.view`). Same reasoning as
   * `nav.whats-new`: the gate exists for the nav invariant, not to restrict
   * who sees the group.
   */
  | 'nav.customers'
  /**
   * Support: the customer support agent surface (cases, live chat, and the
   * knowledge base film library that grounds the agent's answers). Stampede
   * staff only while it is being built, so the same capability gates the nav
   * item and the routes and nobody arrives by URL. To release to venues, swap
   * the rule in `access.policy.ts` and drop the nav badge.
   */
  | 'nav.support'

  // ── Internal / Stampede staff ──
  /** Any internal-domain user. The blanket check the 42 inline copies made. */
  | 'internal.staff'
  /** The `/admin` console: staff email AND oauth role 0 AND 2FA enabled. */
  | 'internal.admin-console'
  /** Loyalty points catch-up (backfill missed points-for-spend): oauth role 0. */
  | 'loyalty.points-catchup'
  /** Engineering agent dock and "Ask engineering". */
  | 'internal.engineering'
  /** Bookings Boost — staff-operated growth surface. */
  | 'internal.boost'
  /** Staff-only diagnostics bolted onto customer reports. */
  | 'internal.debug'
  /** The new-UI opt-in toggle. Staff only, then per-browser localStorage. */
  | 'internal.new-ui-toggle'

  // ── Plan / subscription ──
  | 'plan.bookings'
  | 'plan.contacts'
  | 'plan.gift-cards'
  | 'plan.gift-cards-plus'
  | 'plan.loyalty'
  | 'plan.marketing'
  | 'plan.marketing-plus'
  | 'plan.reviews'
  | 'plan.wifi'
  | 'plan.wifi-plus'

  // ── Org / account administration ──
  /**
   * The organisation account section — billing, team, settings, customer tags,
   * brand kits, event log, everything under `account/organisation/*`.
   *
   * Admin-only. Deliberately does NOT cover `account/profile` or
   * `account/notifications`: those are the user's OWN settings (password, PIN,
   * notification preferences) and every role needs them, floor staff included.
   * Gate the `organisation` subtree, never the `account` parent.
   */
  | 'org.account'
  | 'org.settings.edit'
  | 'org.team.invite'
  | 'org.billing.view'
  | 'org.venues.add'
  | 'org.switch'
  | 'venue.settings.edit'

  // ── Marketing ──
  | 'marketing.ai-email-templates'
  | 'marketing.audiences.classic'
  | 'marketing.audiences.new'
  | 'marketing.audiences.delete-data'
  | 'marketing.campaigns.approve'
  | 'marketing.workflow-logs'

  // ── Bookings ──
  /**
   * The day-to-day operator views: Day Planner, List View, Floorplan. Everyone
   * who can reach Bookings at all gets these — they are the job, for FOH.
   */
  | 'bookings.views'
  | 'bookings.enquiries'
  | 'bookings.deposits'
  /**
   * Issue a refund against a deposit or a cancellation charge, on a booking or
   * an enquiry. Deliberately the same rule as `bookings.deposits`, not a
   * narrower one: whoever is trusted to take the money is trusted to give it
   * back, and splitting the two would leave a Bookings Manager able to charge a
   * guest but not to correct their own mistake.
   *
   * This is the ONLY gate on refunding. Zion enforces tenancy (a refund cannot
   * cross orgs) but no role check, matching every other capability here, so
   * hiding the control is what stops front-of-house issuing refunds.
   */
  | 'bookings.refund'
  | 'bookings.reports'
  | 'bookings.outstanding-payments'
  /** Areas, tables, table combinations — the room layout config. */
  | 'bookings.layout'
  /** Menus and pre-ordering. */
  | 'bookings.menus'
  /**
   * Everything under Settings: main settings, landing page, booking types,
   * special dates, events, upgrades, alerts.
   */
  | 'bookings.settings'
  /**
   * Authority to override the venue's booking guardrails — book outside
   * availability, exceed the configured party-size limits, and pick a booking
   * type that isn't valid for the day.
   *
   * Only half the question: guardrails apply at all only when the venue sets
   * `day_planner_allow_override === false`. The call site combines the two:
   *
   * ```ts
   * const canOverride = !guardrailsEnabled || can('bookings.override-guardrails')
   * ```
   *
   * The venue config deliberately stays out of the policy — it is per-venue data
   * that would drag a booking-config fetch into the subject for every user.
   */
  | 'bookings.override-guardrails'
  /**
   * Waive or edit a mandatory deposit. Wider than
   * `bookings.override-guardrails`: reservations staff (`booking`) need it to
   * take card-less bookings even with guardrails on.
   */
  | 'bookings.override-deposit'
  | 'bookings.floorplan.edit'
  | 'bookings.floorplan.mobile-layout'
  | 'bookings.waitlist'
  | 'bookings.day-planner.new-grid'
  | 'bookings.list-view-3'
  | 'bookings.venue-music.demo'
  /**
   * The AI voice agent surface: number provisioning and call history for a
   * venue. Staff-only while the platform is piloted; widen to
   * `bookings.settings` when venues self-serve.
   */
  | 'bookings.voice'
  /**
   * Ticketed Events: the admission-based event type, its ticket list and door
   * scanner. Released: anyone holding `bookings.settings`.
   */
  | 'bookings.ticketed-events'
  /**
   * Enquiries workspace: the full-page enquiries app with its own rail (zion
   * ENQUIRIES_WORKSPACE_ARD.md D17). Staff plus the named early-access users
   * listed in the policy while it is built. Widen the rule to
   * `bookings.enquiries` to release it.
   */
  | 'bookings.enquiries-workspace'

  // ── Reviews ──
  /**
   * The org-wide reviews surface, as opposed to a single venue's. Granted to a
   * flat set that notably excludes GM and Bookings Manager — venue-level roles
   * get their venue, not the org roll-up.
   */
  | 'reviews.org-level'
  | 'reviews.ai-analytics'

  // ── Reports ──
  | 'reports.full-bhuna'

  // ── Customers ──
  /**
   * Know your customer: when guests order, what they spend and what they buy,
   * read from till orders through `GET /reporting/summary`'s `customers`
   * domain. Square-backed like Full Bhuna, so without an integration there is
   * nothing to show.
   */
  | 'kyc.view'

  // ── Wi-Fi ──
  | 'wifi.portal-payments'
  | 'wifi.surveys'

  // ── Brand ──
  | 'brand.kit-generation'

  // ── Chat ──
  | 'chat.agent-mode'

/**
 * Everything a policy rule may read, resolved once per tree.
 *
 * Resolving this costs roughly six RTK queries, which is exactly why it is
 * resolved ONCE behind a provider rather than per call site. Two independent
 * `NavContext` builders already proved what happens when the same context is
 * assembled twice: a gate added to one silently closes the other.
 */
export interface AccessSubject {
  // ── Axis 1: org role ──
  /** Undefined until the org-role query resolves. */
  role?: RoleKey
  /**
   * `legacy_id` — LOWER is more senior (super = 0). Used for the
   * "may only invite at or below your own level" rule.
   */
  roleLevel?: number

  // ── Axis 2: internal staff ──
  email?: string
  /** Email on the internal `\@stampede.ai` domain. */
  isStaff: boolean
  /** Staff AND oauth `role === 0`. The stricter of the two staff definitions. */
  isSuperAdmin: boolean
  has2FA: boolean
  /**
   * This user owns the selected organisation (`organisation.owner_id`).
   *
   * Independent of the org role: an owner may hold no admin role at all and
   * still needs to run their own org. `team.tsx` already carried this as an
   * `|| user.uid === owner_id` escape hatch on invites; without it in the
   * subject, porting that check would quietly lock owners out.
   */
  isOrgOwner: boolean

  // ── Axis 3: explicit opt-ins ──
  /** Per-browser localStorage opt-in to the v4 UI. Staff-gated on top. */
  newUiOptIn: boolean

  // ── Axis 4: plan ──
  /** `subscription.features`. Empty until billing resolves. */
  planFeatures: Record<string, boolean>
  isTrial: boolean

  // ── Axis 5: account / venue shape ──
  /**
   * The organisation in the URL. Only for the per-organisation early-access
   * lists in the policy; a rule keyed on it is a rollout switch, not a
   * permission, and should be widened to a real capability once released.
   */
  orgId?: string
  dataProviders: string[]
  hasSquare: boolean
  hardwareTier?: HardwareTier
  vendorKey?: VendorType
  /**
   * Serials this user is restricted to, or `null` for no restriction.
   *
   * From `user.location_access`. Not a `Capability` because the question is
   * parameterised by data — see `venue-access.ts` for why, and for the
   * unrestricted-vs-restricted-to-nothing distinction.
   */
  accessibleSerials: string[] | null
  /** The venue in the URL is on a paid network. */
  isPaidVenue: boolean
  hasVenue: boolean
  hasScheme: boolean
  pendingInvitations: number

  /**
   * False while the queries behind the subject are still in flight.
   *
   * Capability rules must not use this to fail OPEN — a gate that flickers
   * open then shut leaks the very thing it guards. It exists so a consumer can
   * render a skeleton instead of an empty list, which is the honest way to
   * handle "we do not know yet".
   */
  isResolved: boolean
}

/**
 * A subject that holds nothing.
 *
 * Two jobs: it is the `AccessProvider`'s default, so a component rendered
 * outside the provider fails closed rather than throwing or silently allowing;
 * and it is the base every test builds on.
 *
 * It lives here, beside the interface it satisfies, rather than in
 * `use-access-subject.ts` — importing that module pulls in the hooks, the RTK
 * slices and ultimately `window`, which makes the pure policy untestable in a
 * node environment.
 */
export const EMPTY_SUBJECT: AccessSubject = {
  isStaff: false,
  isSuperAdmin: false,
  has2FA: false,
  isOrgOwner: false,
  newUiOptIn: false,
  planFeatures: {},
  isTrial: false,
  dataProviders: [],
  hasSquare: false,
  // `null` = unrestricted, matching the data convention. Note this single field
  // cannot fail closed: an unloaded user and an unrestricted one look identical
  // in `location_access`. Callers gate on `isResolved` before acting — see
  // `VenueAccessBoundary`.
  accessibleSerials: null,
  isPaidVenue: false,
  hasVenue: false,
  hasScheme: false,
  pendingInvitations: 0,
  isResolved: false,
}
